<template>
  <Drawer
    :open="visible"
    :title="fileName"
    placement="right"
    width="1100px"
    @close="handleClose"
  >
    <div class="file-preview-container">
      <div class="preview-content">
        <!-- 图片预览 -->
        <div v-if="isImage" class="preview-image-container">
          <img v-if="previewBlobUrl" :src="previewBlobUrl" class="preview-image" title="图片预览" />
          <div v-else class="image-loading">
            <a-spin tip="加载中..." :size="40" />
          </div>
        </div>
        
        <!-- PDF预览 -->
        <div v-else-if="isPdf" class="preview-pdf-container">
          <iframe v-if="previewBlobUrl" :src="previewBlobUrl" class="preview-pdf" title="PDF预览"></iframe>
          <div v-else class="pdf-loading">
            <a-spin tip="加载中..." :size="40" />
          </div>
          <div class="pdf-tip">提示：如果PDF无法正常显示，请下载查看</div>
          <a-button class="pdf-download-btn" @click="handleDownload" :loading="downloading">
            {{ downloading ? '下载中...' : '下载文件' }}
          </a-button>
        </div>
        
        <!-- 其他格式 - 下载 -->
        <div v-else-if="fileUrl" class="download-container">
          <div class="download-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </div>
          <div class="download-text">该文件不支持在线预览</div>
          <a-button type="primary" class="download-btn" @click="handleDownload" :loading="downloading">
            {{ downloading ? '下载中...' : '下载文件' }}
          </a-button>
        </div>
        
        <!-- 无文件 -->
        <div v-else class="no-file">
          无文件可预览
        </div>
      </div>
    </div>
  </Drawer>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { Drawer, Spin, message } from 'ant-design-vue';
import { previewFile, downloadFile } from '@/api/file';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  fileUrl: {
    type: String,
    default: ''
  },
  fileName: {
    type: String,
    default: '文件预览'
  }
});

const emit = defineEmits(['update:visible', 'close']);

const previewBlobUrl = ref('');
const downloading = ref(false);

const handleClose = () => {
  emit('update:visible', false);
  emit('close');
};

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
const pdfExtension = ['pdf'];
const previewableExtensions = [...imageExtensions, ...pdfExtension];

const getExtension = (name) => {
  if (!name) return '';
  const lastDotIndex = name.lastIndexOf('.');
  if (lastDotIndex === -1) return '';
  return name.slice(lastDotIndex + 1).toLowerCase();
};

const fileExtension = computed(() => {
  let ext = getExtension(props.fileName);
  if (!ext && props.fileUrl) {
    ext = getExtension(props.fileUrl);
  }
  return ext;
});

const isImage = computed(() => {
  return imageExtensions.includes(fileExtension.value);
});

const isPdf = computed(() => {
  return pdfExtension.includes(fileExtension.value);
});

const isPreviewable = computed(() => {
  return previewableExtensions.includes(fileExtension.value);
});

const loadFile = async () => {
  if (!props.fileUrl || !isPreviewable.value) return;
  
  previewBlobUrl.value = '';
  
  try {
    const response = await previewFile(props.fileUrl);
    
    if (response && (response instanceof Blob || response instanceof ArrayBuffer)) {
      const blob = response instanceof ArrayBuffer ? new Blob([response]) : response;
      previewBlobUrl.value = window.URL.createObjectURL(blob);
    } else {
      message.error('文件加载失败');
    }
  } catch (error) {
    console.error('文件加载失败:', error);
    message.error('文件加载失败，请重试');
  }
};

watch(() => props.visible, (newVal) => {
  if (newVal && isPreviewable.value) {
    loadFile();
  }
});

const handleDownload = async () => {
  if (!props.fileUrl) return;
  
  downloading.value = true;
  
  try {
    const response = await downloadFile(props.fileUrl);
    
    if (response && (response instanceof Blob || response instanceof ArrayBuffer)) {
      const blob = response instanceof ArrayBuffer ? new Blob([response]) : response;
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = props.fileName || 'download';
      document.body.appendChild(link);
      link.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      message.success('下载成功');
    } else {
      message.error('下载失败');
    }
  } catch (error) {
    console.error('下载失败:', error);
    message.error('下载失败，请重试');
  } finally {
    downloading.value = false;
  }
};
</script>

<style scoped lang="less">
.file-preview-container {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.preview-content {
  width: 100%;
  height: calc(100vh - 180px);
  display: flex;
  flex-direction: column;
}

.preview-image-container {
  flex: 1;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.image-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-pdf-container {
  flex: 1;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.preview-pdf {
  flex: 1;
  width: 100%;
  min-height: 600px;
  border: none;
}

.pdf-tip {
  padding: 8px 16px;
  background: #fffbe6;
  border-top: 1px solid #ffe58f;
  font-size: 12px;
  color: #ad8b00;
}

.pdf-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-download-btn {
  margin: 8px 16px;
  align-self: flex-end;
}

.download-container {
  flex: 1;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.download-icon {
  width: 80px;
  height: 80px;
  color: #999;
}

.download-text {
  font-size: 14px;
  color: #666;
}

.download-btn {
  margin-top: 10px;
}

.no-file {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #999;
}
</style>